import React, { useState } from 'react';
import { Col, Row, Spinner } from '@/components/ui';
import type { PcrMetricsResponse } from '../../../services/pcrService';
import '../../quantified-tracking/components/AggregationCard.css';

interface PcrMetricsSummaryCardProps {
  metrics: PcrMetricsResponse | null;
  loading?: boolean;
  /** Merged into the root card (e.g. `h-100` for a column layout). */
  className?: string;
}

const PcrMetricsSummaryCard: React.FC<PcrMetricsSummaryCardProps> = ({
  metrics,
  loading = false,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const rootClass = (extra: string) =>
    ['tw-flex tw-flex-col tw-bg-surface', 'tw-mb-3', extra, 'aggregation-card', className].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className={rootClass('')}>
        <div className="tw-p-4 aggregation-loading">
          <div className="tw-flex tw-justify-between tw-items-center">
            <h6 className="tw-mb-0">
              <i className="bi bi-graph-up tw-mr-2" />
              Summary Totals
            </h6>
            <Spinner animation="border" size="sm">Loading...</Spinner>
          </div>
        </div>
      </div>
    );
  }

  const pcrNew = metrics?.['new'] ?? 0;
  const internalReview = metrics?.internal_review ?? 0;
  const finalized = metrics?.finalized ?? 0;
  const total = metrics?.total ?? 0;

  return (
    <div className={rootClass('tw-shadow-sm')}>
      <div className="tw-p-4 aggregation-card-header tw-py-3" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="tw-flex tw-justify-between tw-items-center">
          <h6 className="tw-mb-0">
            <i className="bi bi-graph-up tw-mr-2 tw-text-primary" />
            Summary Totals
          </h6>
          <div className="tw-flex tw-items-center">
            <span className="tw-text-content-muted tw-mr-2 tw-text-sm">{isExpanded ? 'Click to collapse' : 'Click to expand'}</span>
            <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} tw-text-primary`} />
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="tw-p-4 tw-border-t tw-border-line tw-bg-surface-alt collapse-transition">
          <Row>
            <Col lg={3} md={6}>
              <div className="tw-flex tw-flex-col tw-h-full tw-rounded tw-bg-warning/10 aggregation-metric">
                <div className="tw-p-4 tw-text-center">
                  <i className="bi bi-envelope-open metric-icon tw-text-warning-text" />
                  <h6 className="tw-text-warning-text">New</h6>
                  <h4 className="metric-value tw-text-warning-text tw-mb-0">{pcrNew}</h4>
                </div>
              </div>
            </Col>
            <Col lg={3} md={6}>
              <div className="tw-flex tw-flex-col tw-h-full tw-rounded tw-bg-info/10 aggregation-metric">
                <div className="tw-p-4 tw-text-center">
                  <i className="bi bi-hourglass-split metric-icon tw-text-info" />
                  <h6 className="tw-text-info">Internal Review</h6>
                  <h4 className="metric-value tw-text-info tw-mb-0">{internalReview}</h4>
                </div>
              </div>
            </Col>
            <Col lg={3} md={6}>
              <div className="tw-flex tw-flex-col tw-h-full tw-rounded tw-bg-success/10 aggregation-metric">
                <div className="tw-p-4 tw-text-center">
                  <i className="bi bi-check-circle metric-icon tw-text-success" />
                  <h6 className="tw-text-success">Finalized</h6>
                  <h4 className="metric-value tw-text-success tw-mb-0">{finalized}</h4>
                </div>
              </div>
            </Col>
            <Col lg={3} md={6}>
              <div className="tw-flex tw-flex-col tw-h-full tw-rounded tw-bg-secondary/10 aggregation-metric">
                <div className="tw-p-4 tw-text-center">
                  <i className="bi bi-collection metric-icon tw-text-secondary" />
                  <h6 className="tw-text-secondary">Total (filters)</h6>
                  <h4 className="metric-value tw-text-secondary tw-mb-0">{total}</h4>
                </div>
              </div>
            </Col>
          </Row>
          <div className="tw-mt-3 tw-text-center">
            <small className="tw-text-content-muted">
              <i className="bi bi-info-circle tw-mr-1" />
              Totals use the same filters as the table (including status). They include all matching rows, not only this
              page.
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default PcrMetricsSummaryCard;
